/*
 * FILE: unified_ui/src/lib/sessionToken.ts
 * PURPOSE: Carry the signed session as `Authorization: Bearer …` for embedded
 *          clients. The web versions of Max/Telegram (and the Max desktop
 *          client) open the SPA in a third-party iframe where browsers drop
 *          our cookies; the header is the fallback webBridge accepts alongside
 *          the `bs_session` cookie (same signed token, same TTL).
 * OWNS: token storage (memory + sessionStorage), the auth-header helper.
 * EXPORTS: setSessionToken, getSessionToken, authHeaders.
 * DOCS: .agents/reports/plan_webview-bearer-session_2026-10-07.md
 */

const STORAGE_KEY = "bs.sessionToken";

let memoryToken: string | null = null;

try {
  memoryToken = typeof window !== "undefined" ? window.sessionStorage.getItem(STORAGE_KEY) : null;
} catch {
  // Storage unavailable (privacy mode) — memory only.
  memoryToken = null;
}

/** Store the token from an auth response. Pass null/undefined to clear. */
export function setSessionToken(token: string | null | undefined): void {
  memoryToken = typeof token === "string" && token.length > 0 ? token : null;
  try {
    if (memoryToken) window.sessionStorage.setItem(STORAGE_KEY, memoryToken);
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — memory only.
  }
}

export function getSessionToken(): string | null {
  return memoryToken;
}

/** Headers for an authenticated API call: the bearer token when we have one. */
export function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...(extra ?? {}) };
  if (memoryToken) headers.Authorization = `Bearer ${memoryToken}`;
  return headers;
}
